import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgClass } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subscription } from 'rxjs';
import { RdsIconComponent } from '@rds/angular-components';
import { Congress } from '../../../models/congress.model';
import { DateFormatter } from '../../../models/date-formatter.model';
import { DetailedViewObjectService } from '../../../services/detailed-view-object.service';
import { CongressViewPreferenceService, CongressViewMode } from '../../../services/congress-view-preference.service';

export type CongressMilestoneType = 'event' | 'abstractDue' | 'lateBreaking';

@Component({
  selector: 'app-congress-milestone',
  standalone: true,
  imports: [
    RdsIconComponent,
    NgClass,
  ],
  templateUrl: './congress-milestone.component.html',
  styleUrls: ['./congress-milestone.component.scss'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('200ms ease-in', style({ height: '0', opacity: 0 }))
      ])
    ])
  ]
})
export class CongressMilestoneComponent implements OnInit, OnDestroy {
  @Input() congress!: Congress;
  @Input() milestoneType: CongressMilestoneType = 'event';

  isExpanded = false;
  viewMode: CongressViewMode = 'drawer';
  private viewModeSub!: Subscription;

  constructor(
    private detailedViewObjectService: DetailedViewObjectService,
    private congressViewPreference: CongressViewPreferenceService,
  ) {}

  ngOnInit() {
    this.viewModeSub = this.congressViewPreference.mode$.subscribe(
      mode => this.viewMode = mode
    );
  }

  ngOnDestroy() {
    this.viewModeSub?.unsubscribe();
  }

  onCardClick() {
    if (this.viewMode === 'drawer') {
      this.detailedViewObjectService.nextDetailedObject(this.congress);
    } else {
      this.isExpanded = !this.isExpanded;
    }
  }

  get milestoneTypeLabel(): string {
    switch (this.milestoneType) {
      case 'event': return 'Event';
      case 'abstractDue': return 'Abstract Deadline';
      case 'lateBreaking': return 'Late Breaking Abstract Deadline';
    }
  }

  get milestoneTypeClass(): string {
    switch (this.milestoneType) {
      case 'event': return 'event';
      case 'abstractDue': return 'abstract';
      case 'lateBreaking': return 'latebreaking';
    }
  }

  get formattedDate(): string {
    const dateStr = this.getMilestoneDate();
    return this.formatDate(dateStr);
  }

  get isDateConfirmed(): boolean {
    switch (this.milestoneType) {
      case 'event': return this.congress.milestones.event.startDateConfirmed;
      case 'abstractDue': return this.congress.milestones.abstractDue.confirmed;
      case 'lateBreaking': return this.congress.milestones.lateBreaking.confirmed;
    }
  }

  get locationText(): string {
    const event = this.congress.milestones.event;
    const parts = [event.city, event.country].filter(Boolean);
    return parts.join(', ');
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    return new DateFormatter(new Date(dateStr), false).presentationDate;
  }

  private getMilestoneDate(): string | null {
    switch (this.milestoneType) {
      case 'event': return this.congress.milestones.event.startDate;
      case 'abstractDue': return this.congress.milestones.abstractDue.date;
      case 'lateBreaking': return this.congress.milestones.lateBreaking.date;
    }
  }
}
